import { useTranslation } from "react-i18next"

/**
 * Utility function to get localized error message for fact check error codes.
 *
 * @param errorCode - The error code from the backend
 * @param t - Translation function from useTranslation hook
 * @returns Localized error message or fallback message
 */
export function getFactCheckErrorMessage(
  errorCode: string | null | undefined,
  t: (key: string) => string
): string {
  if (!errorCode) {
    return ""
  }

  // Try to get the localized error message
  const translationKey = `fact_check.errors.${errorCode}`
  const localizedMessage = t(translationKey)

  // If translation exists and is different from the key, return it
  if (localizedMessage && localizedMessage !== translationKey) {
    return localizedMessage
  }

  // Fallback to a generic error message
  return t("fact_check.errors.FACT_CHECK_WORKFLOW_ERROR")
}

/**
 * Hook to get localized error message for fact check error codes.
 *
 * @param errorCode - The error code from the backend
 * @returns Localized error message or fallback message
 */
export function useFactCheckErrorMessage(errorCode: string | null | undefined): string {
  const { t } = useTranslation()
  return getFactCheckErrorMessage(errorCode, t)
}

/**
 * Type for fact check error codes from the backend.
 */
export type FactCheckErrorCode =
  | "FACT_CHECK_MESSAGE_NOT_FOUND"
  | "FACT_CHECK_SESSION_NOT_AVAILABLE"
  | "FACT_CHECK_SESSION_NOT_FOUND"
  | "FACT_CHECK_ASSISTANT_RESPONSE_NOT_FOUND"
  | "FACT_CHECK_ACCESS_DENIED"
  | "FACT_CHECK_ANSWER_TOO_SHORT"
  | "FACT_CHECK_NO_SOURCES_AVAILABLE"
  | "FACT_CHECK_NO_SOURCES_CAPTURED"
  | "FACT_CHECK_NO_EVIDENCE_FOUND"
  | "FACT_CHECK_NO_EVIDENCE_AVAILABLE"
  | "FACT_CHECK_WORKFLOW_ERROR"
  | "FACT_CHECK_CLAIM_EXTRACTION_ERROR"
  | "FACT_CHECK_CLAIM_SCORING_ERROR"
  | "FACT_CHECK_COMMENT_GENERATION_ERROR"
  | "FACT_CHECK_PROMPT_EVALUATION_ERROR"
  | "FACT_CHECK_MODEL_NOT_FOUND"
  | "FACT_CHECK_NO_MODELS_AVAILABLE"
